/**
 * Crash Capture
 *
 * Persiste erros globais (`window.onerror`, `unhandledrejection`) no
 * localStorage pra serem exibidos no overlay debug do próximo boot.
 *
 * Por quê:
 * Em Samsung Tizen consumer (UN50U8600F, Tizen 7.7) o sdb dlog é
 * bloqueado e o app crashou mais de uma vez sem deixar pista visível.
 * Esse módulo registra cada erro com timestamp, mensagem, stack e
 * source, e expõe via `getRecentCrashes()` pra qualquer componente
 * (ex: overlay debug do Stream).
 *
 * Não substitui ferramenta APM (LogRocket etc) — é um fallback minimal
 * pra debug em campo sem acesso a logs do device.
 */
// `changeSpy` é puro localStorage (zero API de plataforma) — importar aqui não quebra o
// contrato de platform-agnostic deste módulo.
import { getBootId, getBootSeq } from '~/services/diagnostics/changeSpy';
// `logRingBuffer` é um buffer circular in-memory PURO (intercepta console.*, zero API de
// plataforma) — importar aqui mantém o contrato platform-agnostic (mesmo racional do changeSpy).
// Usado pra anexar o breadcrumb das últimas linhas de log ao crash ("o que estava acontecendo antes").
import { logRingBuffer } from '~/services/logging/LogRingBuffer';
const STORAGE_KEY = 'efflux_crash_log_v1';
const HEARTBEAT_KEY = 'efflux_alive_heartbeat_v1';
const HEARTBEAT_HISTORY_KEY = 'efflux_alive_heartbeat_history_v1';
/**
 * Versão do app em build-time (injetada pelo Vite via `VITE_APP_VERSION`).
 *
 * <p><b>Proteção B</b> (supressão na ORIGEM do falso `silent_crash` pós-OTA). A instalação
 * de um OTA mata o app abruptamente (sem `beforeunload`/`pagehide`), então o heartbeat fica
 * órfão e o boot seguinte inferiria um `silent_crash` — que na verdade é o restart do update.
 * Carimbando a versão em cada heartbeat, o boot compara a versão do heartbeat órfão com a atual:
 * se diferem, o app foi SUBSTITUÍDO (update), não crashou → suprime o registro na origem (não
 * vira PLAYER_CRASH, não gera tráfego). Complementa a Proteção A (server), que é a rede de
 * segurança retroativa pra frota que ainda não tem este build.</p>
 */
const APP_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) || '';
/**
 * Proteção D — marcador de restart ESPERADO (comandado pelo admin: restart/reboot).
 *
 * <p>Quando o player recebe um comando de restart/reboot (via SSE {@code restart_command} ou
 * RemoteOps {@code device:reboot}), ele grava este marcador ANTES de reiniciar. Um reboot mata o
 * app abruptamente (igual ao OTA) → deixaria heartbeat órfão → falso {@code silent_crash} no boot.
 * O {@code detectSilentCrash} vê o marcador e suprime NA ORIGEM. Espelha a Proteção B (versão), mas
 * pro restart comandado. Complementa a Proteção D no server (que marca pela frota, sem OTA).</p>
 */
const EXPECTED_RESTART_KEY = 'efflux_expected_restart_v1';
/** Janela do marcador de restart esperado (ms). Cobre o boot+detect após o reboot. */
const EXPECTED_RESTART_WINDOW_MS = 5 * 60 * 1000;
// Timestamp (ms) do crash mais recente já reportado pro servidor. Entries com
// `timestamp` > este valor ainda não subiram → o CrashReporter posta e avança
// o marcador. Garante "1 POST por crash" mesmo entre reloads.
const REPORTED_KEY = 'efflux_crash_reported_at_v1';
// Breadcrumb da última operação SÍNCRONA do AVPlay (escrito por tizenAvplay.markAvplayOp).
// Se o app travar DENTRO de uma chamada síncrona bloqueante (open/prepare/stop/close/play),
// este valor fica com a op que não completou → revela onde a main thread travou no hang.
const AVPLAY_OP_KEY = 'efflux_avplay_last_op_v1';
// Contadores CUMULATIVOS do ciclo AVPlay na sessão (escrito por tizenAvplay.bumpAvplayCounter).
// Amostrado a cada heartbeat pra correlacionar "morreu após N opens/closes" com o crash residual
// (op=playing, heap plano, ~a cada 2h). `opens - closes` crescente = players/planos não-fechados
// (leak nativo). Bridge por localStorage (sem import → crashCapture fica platform-agnostic).
const AVPLAY_COUNTERS_KEY = 'efflux_avplay_counters_v1';
// Snapshot da sessão ANTERIOR — preservado pelo tizenAvplay no boot ANTES do reset. É a fonte
// AUTORITATIVA dos contadores NO CRASH (o reset do boot apagava a chave viva antes desta leitura).
const AVPLAY_COUNTERS_PREV_KEY = 'efflux_avplay_counters_prev_v1';
const AVPLAY_TIMING_PREV_KEY = 'efflux_avplay_timing_prev_v1';
// Chave VIVA de latência (escrita pelo tizenAvplay). Preservada → `_prev` no boot ANTES do reset
// da nova sessão (vide preserveCrashTimeSnapshots). Antes era lida como `_prev` mas ninguém
// preservava. `AVPLAY_COUNTERS_KEY` (live) já está declarada acima.
const AVPLAY_TIMING_KEY = 'efflux_avplay_timing_v1';
// Caixa-preta de LONGO HORIZONTE do AVPlay (Fase 0, 2026-07-26). O histórico fino (3s × 10 = 30s)
// só mostra os segundos finais antes da morte; o leak de CMA se desenrola em HORAS até o cliff
// (~630 opens MEDIDOS na bancada — NÃO confiar no número: é o que esta curva viva substitui).
// Amostra GROSSA a cada 60s correlacionando opens × RAM-livre-nativa × heap × playtime → a curva
// inteira da sessão. Sobrevive ao crash (localStorage), lida ao vivo via health:report. Colunas
// curtas p/ caber: t(ts) o(opens) c(closes) e(errors) av(nativeAvailMB) hp(heapUsedMB) pt(playtimeMs).
const AVPLAY_BLACKBOX_KEY = 'efflux_avplay_blackbox_v1';
const AVPLAY_BLACKBOX_PREV_KEY = 'efflux_avplay_blackbox_prev_v1';
const BLACKBOX_INTERVAL_MS = 60_000;
const BLACKBOX_MAX_SAMPLES = 720; // 12h @ 60s
const MAX_ENTRIES = 20;

/**
 * Intervalo de heartbeat. Cada tick salva um snapshot da memória + cache
 * stats no localStorage. Se no próximo boot esse heartbeat for recente
 * (<60s atrás) sem que o app tenha registrado um shutdown limpo,
 * inferimos que houve crash silencioso (OOM nativo do WebView, kill
 * pela OS por janela de input perdida, etc) — esse tipo de morte NÃO
 * dispara `window.onerror` então sem o heartbeat não ficaria registro.
 *
 * v1.0.49 (2026-05-28): reduzido de 15s pra 3s. Em campo (TV Renato),
 * heap=10/347MB no último heartbeat 42s antes do crash — janela cega
 * longa demais. 3s dá ~5x mais granularidade pra capturar spike de
 * memória ou blob URL accumulation logo antes da morte.
 */
const HEARTBEAT_INTERVAL_MS = 3_000;
/**
 * Quantos heartbeats anteriores manter em rotação. Permite ver trend
 * (heap crescente, blob URL acumulando) e não só o último snapshot.
 */
const HEARTBEAT_HISTORY_SIZE = 10;

/**
 * Contadores cumulativos do ciclo de vida do AVPlay na sessão (Tizen). Medem o CHURN por slot pra
 * o diagnóstico do crash residual: `opens - closes` crescente = players/planos de vídeo não
 * fechados (leak nativo do decoder). Escritos por `tizenAvplay`, lidos aqui via localStorage.
 */
export interface AvplayCounters {
  opens: number;
  prepares: number;
  plays: number;
  stops: number;
  closes: number;
  errors: number;
}

/**
 * Amostra da caixa-preta de longo horizonte (Fase 0). Chaves curtas de propósito (o buffer chega a
 * {@link BLACKBOX_MAX_SAMPLES} × ~60 bytes). `t`=timestamp; `o/c/e`=opens/closes/errors cumulativos;
 * `av`=RAM-livre-nativa MB; `hp`=heap-JS MB; `pt`=playtime do slot atual (ms). Campos ausentes fora
 * do Tizen ou antes do 1º dado.
 */
export interface BlackboxSample {
  t: number;
  o?: number;
  c?: number;
  e?: number;
  av?: number;
  hp?: number;
  pt?: number;
  /** C4: recycles acumulados (steps ↑ = remount dos planos). SAWTOOTH em `av` após `r` subir =
   *  o recycle reclama o CMA (Opção A funciona). */
  r?: number;
}

/**
 * Contexto estruturado do crash — além do `message` (string legível), pra o
 * servidor/alerta receber os campos parseáveis (heap trend, vídeo, cache).
 * Metadados de device/versão/plataforma são adicionados pelo CrashReporter na
 * hora do POST (este módulo é baixo-nível e não conhece config).
 */
export interface CrashContext {
  heapUsedMB?: number;
  heapLimitMB?: number;
  /** Heap (MB) das últimas amostras antes do crash — trend de leak/OOM (JS). */
  heapTrend?: number[];
  /** Status de memória NATIVA das últimas amostras (Tizen) — o trend que decide H1 vs H2. */
  nativeMemTrend?: string[];
  /** RAM livre do sistema (MB) das últimas amostras (Tizen) — proxy numérico do leak nativo/CMA. */
  nativeAvailTrend?: number[];
  video?: {
    currentName?: string;
    sourceKind?: 'blob' | 'http' | 'file' | 'none';
    transitioning?: boolean;
    /** Nome da campanha do conteúdo tocando agora (se resolvido barato no heartbeat). */
    currentCampaign?: string;
    /** Kiosk locked no momento (se disponível barato no heartbeat). */
    kioskLocked?: boolean;
  };
  cache?: {
    cached: number;
    downloading: number;
    saving?: number;
    errors: number;
    activeBlobUrls?: number;
  };
  /** Segundos entre o último heartbeat e a detecção no boot (silent crash). */
  ageSec?: number;
  /**
   * Versão do Chrome/WebView (parseada do `navigator.userAgent`, ex.: "120.0.6099.43"). Pro
   * silent_crash é a versão do CRASH (heartbeat órfão); pro erro live é a versão atual. Ausente
   * onde o UA não traz "Chrome/" (ex.: WebKit Tizen). Base da classificação `webview_update` no
   * server (compara a versão reportada entre crashes consecutivos).
   */
  webViewVersion?: string;
  /** Plataforma detectada (tizen/webos/android/browser) — via window.tizen/Capacitor, sem import de plataforma. */
  platform?: string;
  /** Modelo do device parseado do UA (ex.: "SM-T505"), quando disponível. */
  deviceModel?: string;
  /** Duração da sessão (s) até o crash — do 1º heartbeat da sessão até o crash. */
  sessionDurationSec?: number;
  /** Conectividade (navigator.onLine) no momento do crash, se disponível. */
  online?: boolean;
  /** Breadcrumb: últimas ~20 linhas do LogRingBuffer — "o que estava acontecendo antes" (só crash live). */
  logBreadcrumb?: string[];
  /**
   * Última atividade do AVPlay antes do crash (breadcrumb do tizenAvplay). `op` é ou uma
   * chamada SÍNCRONA bloqueante (open/prepare/stop-old/close-past/play/destroy/set-rect/
   * pause/seek → travou DENTRO dela) ou um EVENTO de playback (playing, buffering-start,
   * buffering-complete, stream-completed, avplay-error → JS saudável, morte nativa). O
   * `playtimeMs` = até onde o vídeo chegou. `msBeforeCrash` = há quanto tempo (antes do
   * último heartbeat) a op começou.
   */
  lastAvplayOp?: {
    op: string;
    detail?: string;
    playtimeMs?: number;
    ts: number;
    msBeforeCrash?: number;
  };
  /** Contadores cumulativos do ciclo AVPlay no momento do crash (snapshot do último heartbeat). */
  avplayCounters?: AvplayCounters;
}

export interface CrashEntry {
  timestamp: number;
  message: string;
  stack?: string;
  source?: string;
  line?: number;
  col?: number;
  type: 'error' | 'unhandledrejection' | 'silent_crash';
  /** Confiança da inferência de silent crash (só pra type=silent_crash). */
  confidence?: 'alta' | 'média';
  /** Instante provável do crash (último heartbeat p/ silent; = timestamp p/ erro). */
  occurredAt?: number;
  /** Snapshot estruturado do estado no momento do crash. */
  context?: CrashContext;
}

export interface AliveHeartbeat {
  timestamp: number;
  /**
   * Versão do app rodando neste tick (`VITE_APP_VERSION`). Proteção B: se o heartbeat
   * órfão de um boot anterior traz uma versão diferente da atual, o app foi trocado por
   * um OTA (não crashou) → o `silent_crash` é suprimido na origem. Ausente em builds
   * anteriores à Proteção B (aí a supressão não dispara — Proteção A cobre no server).
   */
  version?: string;
  /**
   * Versão do Chrome/WebView deste tick (parseada do `navigator.userAgent`). Carimbada em cada
   * heartbeat pra permitir detectar mudança entre restarts (base da classificação `webview_update`).
   * Espelha o campo `version` (VITE_APP_VERSION) da Proteção B. Ausente onde o UA não traz "Chrome/".
   */
  webViewVersion?: string;
  /** Instante (ms) do 1º heartbeat da sessão — permite derivar a duração da sessão até o crash. */
  sessionStartedAt?: number;
  /** Conectividade (navigator.onLine) neste tick, se disponível. */
  online?: boolean;
  /** Memória heap em MB (se disponível via performance.memory) */
  heapUsedMB?: number;
  heapLimitMB?: number;
  /** Status da memória NATIVA (Tizen: NORMAL/LOW/CRITICAL). Só Tizen; ausente no resto. */
  nativeMem?: string;
  /** RAM livre do SISTEMA em MB (Tizen `getAvailableMemory`, numérico). Queda monotônica c/ heap
   * plano = leak nativo/CMA. Só Tizen; ausente no resto. */
  nativeAvailMB?: number;
  /** Stats opcionais — populados via setHeartbeatContext() */
  cacheStats?: {
    cached: number;
    downloading: number;
    saving: number;
    errors: number;
    /**
     * Quantos blob URLs estão vivos no IndexedDBCache.activeBlobUrls Map.
     * Cada blob URL alive segura ~40 MB de memória NATIVA no WebView
     * (não JS heap). Em Tizen com cap=2-3, esperado é 1-3.
     * Se aparecer >5 num crash, é candidato forte pra causa raiz.
     */
    activeBlobUrls?: number;
  };
  /** Estado do vídeo no momento (capturado via setHeartbeatContext extras) */
  videoState?: {
    /** Nome do conteúdo tocando agora (debug) */
    currentName?: string;
    /** Source URL atual (blob: ou https:) */
    sourceKind?: 'blob' | 'http' | 'file' | 'none';
    /** Se está num transition entre vídeos (potencial pico de memória) */
    transitioning?: boolean;
    /** Nome da campanha do conteúdo atual (se resolvido barato pelo Stream). */
    currentCampaign?: string;
    /** Kiosk locked no momento (se disponível barato pelo Stream). */
    kioskLocked?: boolean;
  };
  /** Contadores cumulativos do ciclo AVPlay neste tick (Tizen; lidos do localStorage). */
  avCounters?: AvplayCounters;
}

/**
 * Contexto atualizado pelo Stream/MediaStore via {@link setHeartbeatContext}.
 * Mantido em memória entre ticks do heartbeat — o tick lê e persiste.
 */
let heartbeatCacheContext: AliveHeartbeat['cacheStats'] | null = null;
let heartbeatVideoContext: AliveHeartbeat['videoState'] | null = null;
/** Timestamp da última amostra grossa da caixa-preta (cadência {@link BLACKBOX_INTERVAL_MS}). */
let lastBlackboxAt = 0;

/**
 * Último status de memória NATIVA (Tizen: NORMAL/LOW/CRITICAL). O crash de campo é NATIVO
 * (kill da OS ~1GB de footprint no QB50B/3GB) e o `performance.memory` (heap JS) NÃO enxerga
 * isso — fica plano em ~20MB enquanto o nativo estoura. `tizen.systeminfo` é ASSÍNCRONO, então
 * amostramos em background e guardamos o último valor aqui; o heartbeat síncrono lê esta var.
 * Diagnóstico: trend subindo pra LOW/CRITICAL antes do crash = leak nativo (H1); fica NORMAL e
 * morre seco = fault pontual (H2). No-op fora do Tizen (fica null → campo omitido).
 */
let lastNativeMem: string | null = null;

/** Dispara uma leitura async da memória nativa; atualiza {@link lastNativeMem} pro próximo tick. */
function refreshNativeMem(): void {
  try {
    // Acesso direto ao tizen.systeminfo — módulo de diagnóstico de baixo nível (mesmo padrão
    // do performance.memory acima). Guardado: ausente fora do Tizen → catch/no-op.
    const si = (
      window as unknown as {
        tizen?: {
          systeminfo?: {
            getPropertyValue?: (
              prop: string,
              ok: (data: { status?: string }) => void,
              err: () => void,
            ) => void;
          };
        };
      }
    ).tizen?.systeminfo;
    si?.getPropertyValue?.(
      'MEMORY',
      (data) => {
        if (data && typeof data.status === 'string')
          lastNativeMem = data.status;
      },
      () => {
        /* leitura falhou — mantém último valor */
      },
    );
  } catch {
    /* não-Tizen / API indisponível — lastNativeMem fica null */
  }
}

/**
 * RAM livre do sistema em BYTES→MB (SÍNCRONO; `getAvailableMemory` existe desde a API 2.3 → ambos
 * firmwares Tizen). Diferente do status coarse NORMAL/LOW do `refreshNativeMem` acima: é NUMÉRICO,
 * então dá pra ver a queda MONOTÔNICA que confirma o leak de CMA/nativo do decoder (heap JS plano +
 * RAM livre caindo = leak nativo — a assinatura da morte op=playing ~630). Numa TV que só roda o
 * Efflux é o proxy direto da pegada nativa. Device-wide (não por-processo), mas basta num app único.
 * Sem shell/CDP/cert — chamável do nosso próprio JS. No-op fora do Tizen (null → campo omitido).
 */
export function readNativeAvailMB(): number | null {
  try {
    const si = (
      window as unknown as {
        tizen?: { systeminfo?: { getAvailableMemory?: () => number } };
      }
    ).tizen?.systeminfo;
    if (typeof si?.getAvailableMemory !== 'function') return null;
    const bytes = si.getAvailableMemory();
    if (!(bytes > 0)) return null;
    return Math.round(bytes / 1048576);
  } catch {
    return null;
  }
}

/**
 * Instante do 1º heartbeat desta sessão (boot atual). Fixado no {@link installCrashCapture} e
 * carimbado em cada heartbeat pra permitir derivar a DURAÇÃO da sessão até o crash: pro crash live,
 * {@code now - sessionStartedAt}; pro silent_crash, {@code prev.timestamp - prev.sessionStartedAt}
 * (do heartbeat órfão da sessão morta).
 */
let sessionStartedAt = Date.now();

/**
 * Versão do Chrome/WebView parseada do userAgent (ex.: "120.0.6099.43"). Android WebView e browsers
 * Chromium trazem "Chrome/XXX"; WebKit Tizen NÃO → retorna undefined (campo omitido). Puro (regex),
 * sem API de plataforma. Default lê o UA atual; aceita um UA explícito pra testes.
 */
export function parseWebViewVersion(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): string | undefined {
  try {
    const m = /Chrome\/([\d.]+)/.exec(ua);
    return m ? m[1] : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Modelo do device parseado do userAgent (best-effort). Samsung Android/tablet: token "SM-XXXX";
 * senão o modelo antes de "Build/" no UA Android. Puro (regex). undefined quando não dá pra inferir.
 */
export function parseDeviceModel(
  ua: string = typeof navigator !== 'undefined' ? navigator.userAgent : '',
): string | undefined {
  try {
    const sm = /\b(SM-[A-Z0-9]+)\b/.exec(ua);
    if (sm) return sm[1];
    const build = /;\s*([^;)]+?)\s+Build\//.exec(ua);
    if (build) return build[1].trim();
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Plataforma detectada em runtime SEM importar código de plataforma (guardrail): via
 * `window.tizen`/`window.webOS`/`window.Capacitor` (mesmo padrão do `refreshNativeMem`). Retorna
 * valores canônicos: tizen/webos/android/browser.
 */
export function detectPlatformHint(): string {
  try {
    const w = window as unknown as {
      tizen?: unknown;
      webOS?: unknown;
      Capacitor?: { getPlatform?: () => string };
    };
    const cap = w.Capacitor?.getPlatform?.();
    if (cap === 'android' || cap === 'ios') return cap;
    if (w.tizen) return 'tizen';
    if (w.webOS) return 'webos';
    return 'browser';
  } catch {
    return 'browser';
  }
}

/**
 * Últimas {@code lines} linhas do {@link logRingBuffer} formatadas ("ts [level] msg") — o breadcrumb
 * de "o que estava acontecendo antes" do crash. Best-effort: [] se o buffer não estiver acessível.
 */
function readLogBreadcrumb(lines = 20): string[] {
  try {
    return logRingBuffer
      .tail(lines)
      .map((e) => `${e.timestamp} [${e.level}] ${e.message}`);
  } catch {
    return [];
  }
}

/** Conectividade atual (navigator.onLine), ou undefined se indisponível. Barato e síncrono. */
function readOnlineNow(): boolean | undefined {
  try {
    return typeof navigator !== 'undefined' &&
      typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Contexto de um crash LIVE (window.onerror / unhandledrejection): metadados baratos capturados na
 * hora (device/versão/plataforma/duração/conectividade/breadcrumb + último estado de vídeo/cache).
 * NUNCA lança — cada peça é guardada; retorna o que conseguiu montar.
 */
function buildLiveCrashContext(): CrashContext {
  const ctx: CrashContext = {};
  try {
    const wv = parseWebViewVersion();
    if (wv) ctx.webViewVersion = wv;
    ctx.platform = detectPlatformHint();
    const model = parseDeviceModel();
    if (model) ctx.deviceModel = model;
    ctx.sessionDurationSec = Math.max(
      0,
      Math.round((Date.now() - sessionStartedAt) / 1000),
    );
    const online = readOnlineNow();
    if (online !== undefined) ctx.online = online;
    const breadcrumb = readLogBreadcrumb();
    if (breadcrumb.length) ctx.logBreadcrumb = breadcrumb;
    // Último estado conhecido de vídeo/cache (já em memória — barato). Inclui campanha/kiosk se o
    // Stream os forneceu.
    if (heartbeatVideoContext) ctx.video = heartbeatVideoContext;
    if (heartbeatCacheContext) ctx.cache = heartbeatCacheContext;
  } catch {
    /* best-effort — retorna o que já montou */
  }
  return ctx;
}

function safeRead(): CrashEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWrite(entries: CrashEntry[]): void {
  try {
    // Cap to MAX_ENTRIES (keep most recent)
    const trimmed = entries.slice(-MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // localStorage may be full or unavailable — silently ignore
  }
}

function appendCrash(entry: CrashEntry): void {
  const existing = safeRead();
  existing.push(entry);
  safeWrite(existing);
}

/**
 * Returns recent crashes in chronological order (oldest first).
 */
export function getRecentCrashes(): CrashEntry[] {
  return safeRead();
}

/**
 * v1.0.51 — Conta quantas vezes um vídeo específico aparece em
 * silent_crash entries. Usado pra detectar crash loop num conteúdo
 * particular (ex: Prada - Paradoxe crashou 11× em campo) e disparar
 * skip automático no MediaStore.
 *
 * Match é case-insensitive e ignora prefixos do nome (`Trigger ${name}`
 * vira `${name}`). Considera apenas entries das últimas N horas pra
 * não ficar travado em vídeos que já foram corrigidos no admin.
 *
 * @param videoName nome literal do conteúdo (PlaylistItem.name)
 * @param sinceHours janela em horas (default 1h)
 */
export function countCrashesForVideo(
  videoName: string,
  sinceHours = 1,
): number {
  const since = Date.now() - sinceHours * 60 * 60 * 1000;
  const norm = videoName
    .toLowerCase()
    .replace(/^trigger\s+/i, '')
    .trim();
  return safeRead().filter((entry) => {
    if (entry.type !== 'silent_crash') return false;
    if (entry.timestamp < since) return false;
    // A mensagem tem formato: "...video=Nome Completo src=http..."
    const match = entry.message.match(
      /video=([^\s]+(?:\s+[^\s=]+)*?)(?=\s+src=|$)/,
    );
    if (!match) return false;
    const crashedName = match[1]
      .toLowerCase()
      .replace(/^trigger\s+/i, '')
      .trim();
    return crashedName === norm;
  }).length;
}

/**
 * Returns the most recent crash entry, or null.
 */
export function getLastCrash(): CrashEntry | null {
  const entries = safeRead();
  return entries.length > 0 ? entries[entries.length - 1] : null;
}

/**
 * Clears all stored crashes.
 */
export function clearCrashes(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Crashes ainda NÃO reportados pro servidor (timestamp > marcador persistido).
 * O CrashReporter posta esses e chama {@link markCrashesReported}.
 */
export function getUnreportedCrashes(): CrashEntry[] {
  let reportedAt = 0;
  try {
    reportedAt = Number(localStorage.getItem(REPORTED_KEY)) || 0;
  } catch {
    /* ignore */
  }
  return safeRead().filter((e) => e.timestamp > reportedAt);
}

/**
 * Avança o marcador de "reportado" pro timestamp mais recente enviado.
 * Idempotente — só avança (nunca retrocede). Sobrevive a reloads → garante
 * 1 POST por crash mesmo se o app reiniciar várias vezes.
 */
export function markCrashesReported(upToTimestamp: number): void {
  try {
    const cur = Number(localStorage.getItem(REPORTED_KEY)) || 0;
    if (upToTimestamp > cur) {
      localStorage.setItem(REPORTED_KEY, String(upToTimestamp));
    }
  } catch {
    /* ignore */
  }
}

let installed = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Permite que componentes externos (Stream overlay, MediaStore) atualizem
 * o contexto que será gravado no próximo tick do heartbeat. Usado pra que
 * no caso de crash silencioso, o boot seguinte saiba qual era o estado
 * do cache logo antes de morrer.
 */
export function setHeartbeatContext(
  stats: AliveHeartbeat['cacheStats'] | null,
): void {
  heartbeatCacheContext = stats;
}

/**
 * Atualiza o snapshot do estado do vídeo. Chamado pelo Stream quando o
 * vídeo atual muda ou quando começa transição.
 */
export function setHeartbeatVideoState(
  state: AliveHeartbeat['videoState'] | null,
): void {
  heartbeatVideoContext = state;
}

function readHeartbeatHistory(): AliveHeartbeat[] {
  try {
    const raw = localStorage.getItem(HEARTBEAT_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeHeartbeatHistory(entry: AliveHeartbeat): void {
  try {
    const history = readHeartbeatHistory();
    history.push(entry);
    // Mantém só os últimos N pra não inflar localStorage
    const trimmed = history.slice(-HEARTBEAT_HISTORY_SIZE);
    localStorage.setItem(HEARTBEAT_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {
    /* localStorage cheio — ignora */
  }
}

/**
 * Retorna histórico de heartbeats do boot ANTERIOR (capturado antes do
 * crash, se houve). Útil pra debugar trend (memória crescendo, blob URL
 * acumulando, etc).
 */
export function getPreCrashHeartbeatHistory(): AliveHeartbeat[] {
  return readHeartbeatHistory();
}

function readBlackbox(key: string): BlackboxSample[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Anexa uma amostra grossa à caixa-preta de longo horizonte. Chamada pelo heartbeat quando passou
 * {@link BLACKBOX_INTERVAL_MS} do último sample. Só amostra quando há sinal AVPlay/nativo (evita
 * inflar o buffer no browser/Android, onde não há leak de CMA). Ring buffer capado.
 */
function appendBlackboxSample(entry: AliveHeartbeat): void {
  // Sem contadores AVPlay E sem RAM-nativa → nada de leak nativo pra medir; não amostra.
  if (!entry.avCounters && typeof entry.nativeAvailMB !== 'number') return;
  try {
    let playtimeMs: number | undefined;
    try {
      const raw = localStorage.getItem(AVPLAY_OP_KEY);
      if (raw) {
        const o = JSON.parse(raw) as { playtimeMs?: unknown };
        if (typeof o.playtimeMs === 'number')
          playtimeMs = Math.round(o.playtimeMs);
      }
    } catch {
      /* ignore */
    }
    let recycles: number | undefined; // C4: recycles acumulados (bridge por localStorage)
    try {
      const raw = localStorage.getItem('efflux_avplay_recycles_v1');
      if (raw != null) {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n)) recycles = n;
      }
    } catch {
      /* ignore */
    }
    const av = entry.avCounters;
    const sample: BlackboxSample = {
      t: entry.timestamp,
      ...(av ? { o: av.opens, c: av.closes, e: av.errors } : {}),
      ...(typeof entry.nativeAvailMB === 'number'
        ? { av: entry.nativeAvailMB }
        : {}),
      ...(typeof entry.heapUsedMB === 'number' ? { hp: entry.heapUsedMB } : {}),
      ...(typeof playtimeMs === 'number' ? { pt: playtimeMs } : {}),
      ...(typeof recycles === 'number' ? { r: recycles } : {}),
    };
    const buf = readBlackbox(AVPLAY_BLACKBOX_KEY);
    buf.push(sample);
    localStorage.setItem(
      AVPLAY_BLACKBOX_KEY,
      JSON.stringify(buf.slice(-BLACKBOX_MAX_SAMPLES)),
    );
  } catch {
    /* localStorage cheio/indisponível — ignora */
  }
}

/** Caixa-preta de longo horizonte da sessão ATUAL (curva opens × RAM-livre × heap × playtime). */
export function getAvplayBlackbox(): BlackboxSample[] {
  return readBlackbox(AVPLAY_BLACKBOX_KEY);
}

/** Caixa-preta da sessão ANTERIOR (preservada no boot — pós-crash). */
export function getAvplayBlackboxPrev(): BlackboxSample[] {
  return readBlackbox(AVPLAY_BLACKBOX_PREV_KEY);
}

/**
 * Preserva os snapshots crash-time da sessão ANTERIOR ANTES que o tizenAvplay (que carrega depois,
 * no mount do Stream) zere contadores/latência da nova sessão. As chaves `_prev` eram LIDAS como
 * autoritativas mas NUNCA escritas (instrumentação morta — Fase 0 corrige). Copia live→prev de
 * contadores + latência + caixa-preta; depois ZERA a caixa-preta viva pra a nova sessão começar do
 * zero (a curva anterior fica no `_prev`, lida via health:report pós-crash).
 */
function preserveCrashTimeSnapshots(): void {
  const copy = (from: string, to: string): void => {
    try {
      const raw = localStorage.getItem(from);
      if (raw) localStorage.setItem(to, raw);
    } catch {
      /* ignore */
    }
  };
  /**
   * NÃO sobrescrever um `_prev` bom com uma chave viva JÁ ZERADA (F0, 2026-07-27).
   *
   * <p>Corpos de módulo ES avaliam ANTES de `installCrashCapture()` ser chamado. O
   * `tizenAvplay` zera `efflux_avplay_counters_v1` na avaliação dele — então, quando esta
   * função roda, a chave viva pode já valer `{"opens":0,…}`. Copiar isso pro `_prev`
   * DESTRUÍA a contagem da sessão anterior: foi por isso que a sonda CDP de 27/07 achou
   * `counters_prev` todo zerado enquanto o `blackbox_prev` tinha curva real.
   *
   * <p>O `tizenAvplay` agora faz a cópia certa antes de zerar; aqui só garantimos não
   * atropelá-la. Vale só pros contadores (o timing/caixa-preta não são zerados na avaliação).
   */
  const copyUnlessAllZero = (from: string, to: string): void => {
    try {
      const raw = localStorage.getItem(from);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const values = Object.values(parsed);
      const allZero =
        values.length > 0 &&
        values.every((v) => typeof v === 'number' && v === 0);
      if (allZero) return; // sessão nova já zerada — o `_prev` correto já foi gravado
      localStorage.setItem(to, raw);
    } catch {
      /* JSON inválido/indisponível — não arrisca destruir o `_prev` */
    }
  };
  copyUnlessAllZero(AVPLAY_COUNTERS_KEY, AVPLAY_COUNTERS_PREV_KEY);
  copy(AVPLAY_TIMING_KEY, AVPLAY_TIMING_PREV_KEY);
  copy(AVPLAY_BLACKBOX_KEY, AVPLAY_BLACKBOX_PREV_KEY);
  try {
    localStorage.removeItem(AVPLAY_BLACKBOX_KEY); // nova sessão recomeça a curva do zero
  } catch {
    /* ignore */
  }
}

function readHeartbeat(): AliveHeartbeat | null {
  try {
    const raw = localStorage.getItem(HEARTBEAT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.timestamp === 'number' ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Lê os contadores cumulativos do ciclo AVPlay (escritos por `tizenAvplay` no localStorage).
 * Bridge por localStorage → sem import de código de plataforma neste módulo universal. Retorna
 * `null` fora do Tizen / antes do 1º op (chave ausente).
 */
function readAvplayCounters(): AvplayCounters | null {
  try {
    const raw = localStorage.getItem(AVPLAY_COUNTERS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Partial<AvplayCounters>;
    if (typeof o.opens !== 'number') return null;
    return {
      opens: o.opens,
      prepares: o.prepares ?? 0,
      plays: o.plays ?? 0,
      stops: o.stops ?? 0,
      closes: o.closes ?? 0,
      errors: o.errors ?? 0,
    };
  } catch {
    return null;
  }
}

/** Contadores da sessão ANTERIOR (crash-time autoritativo — preservado antes do reset do boot). */
function readAvplayCountersPrev(): AvplayCounters | null {
  try {
    const raw = localStorage.getItem(AVPLAY_COUNTERS_PREV_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Partial<AvplayCounters>;
    if (typeof o.opens !== 'number') return null;
    return {
      opens: o.opens,
      prepares: o.prepares ?? 0,
      plays: o.plays ?? 0,
      stops: o.stops ?? 0,
      closes: o.closes ?? 0,
      errors: o.errors ?? 0,
    };
  } catch {
    return null;
  }
}

/** Trend de latência da sessão ANTERIOR (open→1º-frame / prepare→ready — fingerprint do slowdown). */
function readAvplayTimingPrev(): string | null {
  try {
    const raw = localStorage.getItem(AVPLAY_TIMING_PREV_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as {
      lastPrepMs?: number;
      maxPrepMs?: number;
      lastFrameMs?: number;
      maxFrameMs?: number;
      samples?: number;
    };
    if (!(typeof t.samples === 'number' && t.samples > 0)) return null; // sem frame medido → sem linha
    return ` avTiming=frame(last${t.lastFrameMs ?? '?'}/max${t.maxFrameMs ?? '?'}ms) prep(last${t.lastPrepMs ?? '?'}/max${t.maxPrepMs ?? '?'}ms) n${t.samples ?? 0}`;
  } catch {
    return null;
  }
}

function writeHeartbeat(): void {
  try {
    // performance.memory existe em V8 (Chrome/Tizen/Capacitor Android) mas
    // não no spec — typecheck condicional pra não quebrar TS.
    const perfMem = (
      performance as unknown as {
        memory?: {
          usedJSHeapSize: number;
          jsHeapSizeLimit: number;
        };
      }
    ).memory;

    const avCounters = readAvplayCounters();
    const webViewVersion = parseWebViewVersion();
    const online = readOnlineNow();
    const entry: AliveHeartbeat = {
      timestamp: Date.now(),
      ...(APP_VERSION ? { version: APP_VERSION } : {}),
      ...(webViewVersion ? { webViewVersion } : {}),
      sessionStartedAt,
      ...(online !== undefined ? { online } : {}),
      ...(perfMem
        ? {
            heapUsedMB: Math.round(perfMem.usedJSHeapSize / (1024 * 1024)),
            heapLimitMB: Math.round(perfMem.jsHeapSizeLimit / (1024 * 1024)),
          }
        : {}),
      ...(lastNativeMem ? { nativeMem: lastNativeMem } : {}),
      ...((): { nativeAvailMB?: number } => {
        const mb = readNativeAvailMB(); // síncrono — RAM livre do sistema (proxy do leak nativo)
        return mb != null ? { nativeAvailMB: mb } : {};
      })(),
      ...(heartbeatCacheContext ? { cacheStats: heartbeatCacheContext } : {}),
      ...(heartbeatVideoContext ? { videoState: heartbeatVideoContext } : {}),
      ...(avCounters ? { avCounters } : {}),
    };
    localStorage.setItem(HEARTBEAT_KEY, JSON.stringify(entry));
    // Dispara a leitura async da memória nativa pro PRÓXIMO tick (o valor deste tick já foi
    // gravado acima com o último status conhecido). No-op fora do Tizen.
    refreshNativeMem();
    // Histórico rotativo — preserva os últimos N snapshots pra análise
    // trend pós-crash. Lê do localStorage every tick (3s) — ok porque a
    // operação é local e barata.
    writeHeartbeatHistory(entry);
    // Caixa-preta de longo horizonte (Fase 0): amostra grossa a cada BLACKBOX_INTERVAL_MS (não a
    // cada tick de 3s) pra cobrir HORAS até o cliff de CMA sem inflar o localStorage. 1ª amostra
    // no boot = baseline (lastBlackboxAt=0).
    if (entry.timestamp - lastBlackboxAt >= BLACKBOX_INTERVAL_MS) {
      lastBlackboxAt = entry.timestamp;
      appendBlackboxSample(entry);
      // ALIVE-PING remoto (a cada BLACKBOX_INTERVAL_MS): emite o sample grosso no console →
      // LogRingBuffer → `logs:tail` devolve o trend AO VIVO (RAM nativa + counters AVPlay). O
      // BURACO nesses [BBOX] marca o instante EXATO do freeze (o JS/heartbeat parou) e a última
      // linha mostra o estado logo antes — distingue HANG (app vivo, sync morto) de morte de
      // processo, mesmo com dlog/shell bloqueados no Tizen consumer. Custo: ~1 linha/min.
      try {
        console.info(
          `[BBOX] av=${entry.nativeAvailMB ?? '?'}MB hp=${entry.heapUsedMB ?? '?'}MB ` +
            `avc=${JSON.stringify(entry.avCounters ?? {})}`,
        );
      } catch {
        /* noop */
      }
    }
  } catch {
    /* localStorage cheio / indisponível — ignora */
  }
}

/**
 * Verifica se o heartbeat anterior indica que a sessão prévia NÃO terminou
 * em shutdown limpo. Se sim, registra `silent_crash` no crashLog com
 * snapshot do último estado conhecido.
 *
 * Política: heartbeat existir AT ALL já é sinal de morte súbita (em
 * shutdown limpo via `beforeunload`/`pagehide` ele é removido). O que
 * varia é a confiança:
 *
 *   - **alta** (<90s): crash recente, app reiniciado/reaberto rápido.
 *     Provável OOM nativo do WebView ou kill da OS por input timeout.
 *   - **média** (90s a 24h): provável crash, mas o usuário pode ter
 *     fechado o app via home button (Tizen consumer não chama
 *     `beforeunload` nesse caso). Marca como `silent_crash` mesmo assim
 *     pra ter telemetria.
 *   - **baixa** (>24h): muito velho pra ser confiável — limpa o
 *     heartbeat e não registra.
 *
 * Renato observou em campo (2026-05-28): em TV consumer não tem
 * auto-relaunch, então o usuário só descobre o crash quando volta na
 * frente da TV e clica pra abrir — pode demorar horas. Daí precisamos
 * janela ampla.
 */
/**
 * SESSION_RESTART — registro de TODO boot, com ou sem crash inferido (F0-0b, 2026-07-27).
 *
 * <p>PROBLEMA QUE RESOLVE. O `detectSilentCrash()` abaixo faz `if (!prev) return` — e em saída
 * ORDENADA o `markCleanShutdown` REMOVE o heartbeat. Resultado: encerramentos limpos não deixam
 * rastro nenhum. Medido em 27/07: o `efflux_crash_log_v1` mostrava 4 crashes onde o IndexedDB
 * tinha 10, e um desligamento deliberado não gerou entrada alguma. **As mortes da frota estão
 * subcontadas** — parte dos "player offline" do admin são mortes que a caixa-preta não anotou.
 *
 * <p>Este ring grava um registro por BOOT, independente de inferência. Assim "quantas vezes o app
 * subiu" passa a ser contável, e a diferença entre isso e os crashes registrados É a subcontagem.
 * Ring de {@link SESSION_RESTART_MAX} — a média medida é ~3 mortes/dia, então cobre ~1 semana.
 *
 * <p>Roda ANTES do `detectSilentCrash()` porque ele pode limpar o heartbeat (caminho >24h).
 */
const SESSION_RESTART_KEY = 'efflux_session_restart_v1';
const SESSION_RESTART_MAX = 20;

export interface SessionRestartEntry {
  /** Quando ESTE boot aconteceu. */
  t: number;
  /** Identidade da sessão que está subindo (amarra com a curva da caixa-preta). */
  bootId: string;
  /** Nº sequencial do boot no device — cresce pra sempre; salto = reinstalação. */
  seq: number;
  /** Último heartbeat da sessão anterior. `null` = saiu limpo (heartbeat removido). */
  prevHeartbeatAt: number | null;
  /** Segundos entre o último sinal de vida e este boot ≈ tempo de tela apagada. */
  downSec: number | null;
  /** `true` se o heartbeat órfão existia → `detectSilentCrash` vai registrar crash. */
  crashInferred: boolean;
  /** Contadores FINAIS da sessão anterior (já preservados em `_prev`). */
  prevOpens: number | null;
  /** RAM nativa livre no último heartbeat da sessão anterior (MB). */
  prevNativeAvailMB: number | null;
  /** Última operação AVPlay conhecida — separa morte em `playing` de morte no `prepare-*`. */
  prevLastOp: string | null;
  /**
   * RAM nativa livre NESTE boot, medida na hora do registro — o "DEPOIS" do par.
   *
   * <p>Sem isto não dá pra saber quanto uma reciclagem DEVOLVEU: o `reload_log` só grava o
   * "ANTES" (a RAM no instante da decisão), e a curva da caixa-preta amostra a cada 60s, grosseira
   * demais pra um ciclo de ~70s. Emparelhando `reload_log.freeMB` (antes) com este valor (depois)
   * a conta fica explícita — e é o que separa "o reload devolve ~12MB" de "o reload devolve zero",
   * que os dados de 28/07 não conseguiam distinguir.
   *
   * <p>O `prevNativeAvailMB` NÃO serve pra isso: ele vem do heartbeat da sessão anterior, e saída
   * limpa (reload) REMOVE o heartbeat → vinha sempre nulo nas reciclagens.
   */
  bootNativeAvailMB: number | null;
}

/**
 * Proteção B — o heartbeat órfão veio de uma versão DIFERENTE da atual?
 *
 * <p>Se sim, o app foi trocado por um OTA (a instalação mata o processo sem shutdown limpo →
 * heartbeat órfão), não crashou. Só decide `true` quando o heartbeat anterior TEM versão
 * gravada (builds a partir da Proteção B) E ela difere da versão atual. Sem versão no heartbeat
 * (build antigo), ou versões iguais, retorna `false` → o fluxo normal de `silent_crash` segue
 * (e a Proteção A no server é a rede de segurança).</p>
 */
function isOtaRestart(prev: AliveHeartbeat): boolean {
  return !!APP_VERSION && !!prev.version && prev.version !== APP_VERSION;
}

/**
 * Proteção D — sinaliza que um restart ESPERADO (admin restart/reboot) vai acontecer agora.
 *
 * <p>Chamado pelos handlers de restart do player ANTES de reiniciar ({@code RestartService.handle},
 * {@code RemoteOpsService.handleDeviceReboot}). Grava um marcador com validade curta; no próximo
 * boot, {@code detectSilentCrash} reconhece o restart como comandado (não crash) e suprime na
 * origem. Best-effort — nunca lança.</p>
 */
export function markExpectedRestart(): void {
  try {
    localStorage.setItem(
      EXPECTED_RESTART_KEY,
      JSON.stringify({ until: Date.now() + EXPECTED_RESTART_WINDOW_MS }),
    );
  } catch {
    /* localStorage indisponível — ignora (cai no comportamento pré-D) */
  }
}

/** Proteção D — há um marcador de restart esperado ainda válido? (peek, não remove). */
function isExpectedRestartPending(): boolean {
  try {
    const raw = localStorage.getItem(EXPECTED_RESTART_KEY);
    if (!raw) return false;
    const o = JSON.parse(raw) as { until?: unknown };
    return typeof o.until === 'number' && Date.now() < o.until;
  } catch {
    return false;
  }
}

/** Proteção D — consome (remove) o marcador de restart esperado. */
function clearExpectedRestart(): void {
  try {
    localStorage.removeItem(EXPECTED_RESTART_KEY);
  } catch {
    /* ignore */
  }
}

function recordSessionRestart(bootId: string, seq: number): void {
  try {
    const prev = readHeartbeat();
    const prevCounters = readAvplayCountersPrev();
    let prevLastOp: string | null = null;
    try {
      const rawOp = localStorage.getItem(AVPLAY_OP_KEY);
      if (rawOp) {
        const parsedOp = JSON.parse(rawOp) as { op?: unknown };
        if (typeof parsedOp.op === 'string') prevLastOp = parsedOp.op;
      }
    } catch {
      /* crumb ilegível — segue sem ele */
    }
    // Proteção B/D: se o boot foi um restart ESPERADO — troca de versão (OTA) ou restart comandado
    // pelo admin (marcador) — não conta como crashInferred (a telemetria não infla o nº de crashes).
    const otaRestart =
      !!prev && (isOtaRestart(prev) || isExpectedRestartPending());
    const entry: SessionRestartEntry = {
      t: Date.now(),
      bootId,
      seq,
      prevHeartbeatAt: prev?.timestamp ?? null,
      downSec: prev ? Math.round((Date.now() - prev.timestamp) / 1000) : null,
      crashInferred: !!prev && !otaRestart,
      prevOpens: prevCounters?.opens ?? null,
      prevNativeAvailMB: prev?.nativeAvailMB ?? null,
      prevLastOp,
      bootNativeAvailMB: readNativeAvailMB(),
    };
    const raw = localStorage.getItem(SESSION_RESTART_KEY);
    const list: SessionRestartEntry[] = raw
      ? (JSON.parse(raw) as SessionRestartEntry[])
      : [];
    list.push(entry);
    localStorage.setItem(
      SESSION_RESTART_KEY,
      JSON.stringify(list.slice(-SESSION_RESTART_MAX)),
    );
  } catch {
    /* best-effort — telemetria nunca quebra o boot */
  }
}

/** Histórico de boots (diagnóstico/HUD/testes). Mais recente por último. */
export function getSessionRestarts(): SessionRestartEntry[] {
  try {
    const raw = localStorage.getItem(SESSION_RESTART_KEY);
    return raw ? (JSON.parse(raw) as SessionRestartEntry[]) : [];
  } catch {
    return [];
  }
}

function detectSilentCrash(): void {
  const prev = readHeartbeat();
  if (!prev) return;

  const ageMs = Date.now() - prev.timestamp;
  const ageSec = Math.round(ageMs / 1000);

  // >24h: heartbeat velho demais pra ser sinal útil — limpa silenciosamente.
  if (ageMs > 24 * 60 * 60 * 1000) {
    try {
      localStorage.removeItem(HEARTBEAT_KEY);
    } catch {
      /* ignore */
    }
    return;
  }

  // Proteção B: heartbeat órfão de uma versão anterior = o app foi SUBSTITUÍDO por um OTA
  // (a instalação mata o processo sem shutdown limpo), não crashou. Suprime na ORIGEM — não
  // registra o silent_crash (não vira PLAYER_CRASH nem gera POST). O restart em si já fica no
  // SESSION_RESTART (com crashInferred=false) pra telemetria. Numa frota de milhares, é o que
  // evita a tempestade de falsos crashes a cada OTA — antes mesmo de o evento sair do device.
  if (isOtaRestart(prev)) {
    try {
      localStorage.removeItem(HEARTBEAT_KEY);
    } catch {
      /* ignore */
    }
    console.info(
      `[CrashCapture] ℹ️ Restart de OTA detectado (${prev.version} → ${APP_VERSION}) — ` +
        `silent_crash suprimido na origem (Proteção B)`,
    );
    return;
  }

  // Proteção D: o boot anterior gravou um marcador de restart ESPERADO (admin restart/reboot) antes
  // de reiniciar → o heartbeat órfão é esse restart comandado, não um crash. Suprime na ORIGEM.
  if (isExpectedRestartPending()) {
    clearExpectedRestart();
    try {
      localStorage.removeItem(HEARTBEAT_KEY);
    } catch {
      /* ignore */
    }
    console.info(
      `[CrashCapture] ℹ️ Restart comandado (admin) detectado — silent_crash suprimido na origem (Proteção D)`,
    );
    return;
  }

  const memInfo =
    prev.heapUsedMB && prev.heapLimitMB
      ? ` heap=${prev.heapUsedMB}/${prev.heapLimitMB}MB`
      : '';
  const cacheInfo = prev.cacheStats
    ? ` cache=${prev.cacheStats.cached}c/${prev.cacheStats.downloading}d/${prev.cacheStats.saving ?? 0}s/${prev.cacheStats.errors}e blobs=${prev.cacheStats.activeBlobUrls ?? '?'}`
    : '';
  const videoInfo = prev.videoState
    ? ` video=${prev.videoState.currentName ?? '?'} src=${prev.videoState.sourceKind ?? '?'}${prev.videoState.transitioning ? ' TRANSITIONING' : ''}`
    : '';

  // Trend do heap nas últimas amostras — útil pra ver se memória estava
  // explodindo ou estável. Se heap cresceu rápido = memory leak; se
  // estável = crash externo (codec, native, etc).
  const history = readHeartbeatHistory();
  const trendInfo = (() => {
    if (history.length < 2) return '';
    const heapsMB = history
      .map((h) => h.heapUsedMB)
      .filter((v): v is number => typeof v === 'number');
    if (heapsMB.length < 2) return '';
    const min = Math.min(...heapsMB);
    const max = Math.max(...heapsMB);
    const last = heapsMB[heapsMB.length - 1];
    return ` heapTrend=${min}→${last}MB (max ${max}MB em ${heapsMB.length} amostras)`;
  })();

  // Confiança baseada na idade. <90s = quase certo crash; >90s = pode ser
  // close manual via home button (que em Tizen consumer não dispara
  // beforeunload), mas ainda assim merece registro.
  const confidence = ageMs < 90_000 ? 'alta' : 'média';
  const ageLabel =
    ageSec < 60
      ? `${ageSec}s`
      : ageSec < 3600
        ? `${Math.round(ageSec / 60)}min`
        : `${Math.round(ageSec / 3600)}h`;

  const heapTrend = history
    .map((h) => h.heapUsedMB)
    .filter((v): v is number => typeof v === 'number');

  // Trend da memória NATIVA (Tizen) — O SINAL QUE DECIDE: se subiu pra LOW/CRITICAL antes de
  // morrer = leak nativo/OOM (H1); se ficou NORMAL e morreu seco = fault pontual (H2).
  const nativeMemTrend = history
    .map((h) => h.nativeMem)
    .filter((v): v is string => typeof v === 'string');
  const nativeInfo = nativeMemTrend.length
    ? ` nativeMem=${nativeMemTrend.join('→')}`
    : '';

  // Trend da RAM livre do sistema em MB (numérico) — o proxy DIRETO do leak nativo/CMA: queda
  // monotônica com heap JS plano = memória nativa do decoder vazando até o OS matar.
  const nativeAvailTrend = history
    .map((h) => h.nativeAvailMB)
    .filter((v): v is number => typeof v === 'number');
  const nativeAvailInfo = nativeAvailTrend.length
    ? ` nativeAvailMB=${nativeAvailTrend.join('→')}`
    : '';

  // Breadcrumb da última atividade do AVPlay (localStorage, sobrevive ao restart). Duas
  // classes de `op`:
  //  • SÍNCRONA bloqueante (open/prepare/stop-old/close-past/play/destroy/set-rect/pause/
  //    seek) → se o crumb ficou nela, a main thread travou DENTRO da chamada.
  //  • EVENTO de playback (playing+playtimeMs / buffering-start / buffering-complete /
  //    stream-completed / avplay-error) → o JS estava saudável; morte NATIVA no playback.
  //    `playtimeMs` diz ATÉ ONDE o vídeo chegou (mid-play vs loop dos 16s).
  // `msBeforeCrash` = quanto antes do último heartbeat a op começou.
  const avOp = (() => {
    try {
      const raw = localStorage.getItem(AVPLAY_OP_KEY);
      if (!raw) return null;
      const o = JSON.parse(raw) as {
        op?: unknown;
        detail?: unknown;
        playtimeMs?: unknown;
        ts?: unknown;
      };
      if (typeof o.op !== 'string' || typeof o.ts !== 'number') return null;
      return {
        op: o.op,
        ...(typeof o.detail === 'string' ? { detail: o.detail } : {}),
        ...(typeof o.playtimeMs === 'number'
          ? { playtimeMs: o.playtimeMs }
          : {}),
        ts: o.ts,
        msBeforeCrash: prev.timestamp - o.ts,
      };
    } catch {
      return null;
    }
  })();
  const BLOCKING_OPS = new Set([
    'open',
    'prepare',
    'stop-old',
    'close-past',
    'play',
    'destroy',
    'set-rect',
    'pause',
    'seek',
  ]);
  const avInfo = avOp
    ? ` lastAvplayOp=${avOp.op}${avOp.detail ? `(…${avOp.detail})` : ''}${avOp.playtimeMs != null ? ` playtime=${Math.round(avOp.playtimeMs / 100) / 10}s` : ''} @${Math.round((avOp.msBeforeCrash ?? 0) / 100) / 10}s-antes${BLOCKING_OPS.has(avOp.op) ? ' ⚠️PROVAVEL-HANG-NESTA-CHAMADA-SINCRONA' : avOp.op === 'avplay-error' ? ' ⚠️ERRO-NATIVO-DO-AVPLAY-ANTES-DE-MORRER' : ' (evento de playback — morte nativa durante a reprodução)'}`
    : '';

  // Contadores cumulativos do ciclo AVPlay no crash (churn da sessão). `live = opens - closes` =
  // players/planos de vídeo NÃO fechados; se cresce ao longo da sessão → leak nativo do decoder
  // (hipótese do modo residual op=playing). Δ sobre a janela do histórico = a TAXA de churn logo
  // antes de morrer (ex.: +8 opens em 30s = 1 vídeo a cada ~4s).
  const avCountersHist = history
    .map((h) => h.avCounters)
    .filter((v): v is AvplayCounters => !!v && typeof v.opens === 'number');
  // Prefere a chave _prev (crash-time autoritativo, preservada antes do reset do boot) — o
  // `prev.avCounters`/histórico podem ter sido sobrescritos pelo heartbeat do boot fresco (zerado).
  const avCounters =
    readAvplayCountersPrev() ??
    prev.avCounters ??
    avCountersHist[avCountersHist.length - 1];
  const avTimingInfo = readAvplayTimingPrev() ?? '';

  // Caixa-preta de longo horizonte (Fase 0): resumo compacto da curva opens × RAM-livre da sessão
  // anterior — a assinatura do leak de CMA. A curva INTEIRA fica no `_prev` (health:report).
  const blackboxInfo = (() => {
    const bb = readBlackbox(AVPLAY_BLACKBOX_PREV_KEY);
    if (bb.length < 2) return '';
    const avs = bb
      .map((s) => s.av)
      .filter((v): v is number => typeof v === 'number');
    const opensArr = bb
      .map((s) => s.o)
      .filter((v): v is number => typeof v === 'number');
    const spanH =
      Math.round(((bb[bb.length - 1].t - bb[0].t) / 3_600_000) * 10) / 10;
    const avPart = avs.length
      ? ` av:${avs[0]}→${avs[avs.length - 1]}MB(min${Math.min(...avs)})`
      : '';
    const opPart = opensArr.length
      ? ` opens:${opensArr[0]}→${opensArr[opensArr.length - 1]}`
      : '';
    return ` blackbox=${bb.length}smp/${spanH}h${avPart}${opPart}`;
  })();
  const countersInfo = avCounters
    ? (() => {
        const first = avCountersHist[0];
        const dOpen = first ? avCounters.opens - first.opens : 0;
        const dClose = first ? avCounters.closes - first.closes : 0;
        const live = avCounters.opens - avCounters.closes;
        return ` avCounters=open${avCounters.opens}/prep${avCounters.prepares}/play${avCounters.plays}/stop${avCounters.stops}/close${avCounters.closes}/err${avCounters.errors} live=${live} (Δ${avCountersHist.length}am:+${dOpen}open/+${dClose}close)`;
      })()
    : '';

  // Enriquecimento ADITIVO: metadados crash-time (do heartbeat órfão da sessão morta) + device
  // atual. webViewVersion/online/sessionDuration vêm do `prev` (a sessão que morreu);
  // platform/deviceModel do device (imutáveis entre restarts). Todos best-effort (campo omitido se
  // ausente) — não alteram a inferência do silent_crash.
  const crashWebViewVersion = prev.webViewVersion;
  const crashPlatform = detectPlatformHint();
  const crashDeviceModel = parseDeviceModel();
  const sessionDurationSec =
    typeof prev.sessionStartedAt === 'number'
      ? Math.max(0, Math.round((prev.timestamp - prev.sessionStartedAt) / 1000))
      : undefined;

  appendCrash({
    timestamp: Date.now(),
    type: 'silent_crash',
    confidence,
    occurredAt: prev.timestamp,
    context: {
      ...(prev.heapUsedMB != null ? { heapUsedMB: prev.heapUsedMB } : {}),
      ...(prev.heapLimitMB != null ? { heapLimitMB: prev.heapLimitMB } : {}),
      ...(heapTrend.length ? { heapTrend } : {}),
      ...(nativeMemTrend.length ? { nativeMemTrend } : {}),
      ...(nativeAvailTrend.length ? { nativeAvailTrend } : {}),
      ...(prev.videoState ? { video: prev.videoState } : {}),
      ...(prev.cacheStats ? { cache: prev.cacheStats } : {}),
      ...(avOp ? { lastAvplayOp: avOp } : {}),
      ...(avCounters ? { avplayCounters: avCounters } : {}),
      ...(crashWebViewVersion ? { webViewVersion: crashWebViewVersion } : {}),
      ...(crashPlatform ? { platform: crashPlatform } : {}),
      ...(crashDeviceModel ? { deviceModel: crashDeviceModel } : {}),
      ...(sessionDurationSec !== undefined ? { sessionDurationSec } : {}),
      ...(typeof prev.online === 'boolean' ? { online: prev.online } : {}),
      ageSec,
    },
    message: `App reiniciou ${ageLabel} após último heartbeat (confiança ${confidence}) — provável OOM nativo/kill da OS ou close súbito.${memInfo}${cacheInfo}${videoInfo}${trendInfo}${nativeInfo}${nativeAvailInfo}${avInfo}${countersInfo}${avTimingInfo}${blackboxInfo}`,
  });

  // Diagnóstico forense — appendCrash já persiste no overlay de debug.
  // Console: warn só quando ageMs > 5s E confidence === 'alta' (crash real
  // suspeito). Pra reloads/closes normais (age curta ou confidence média)
  // vai como info pra não poluir console com falso-positivo no boot.
  const realCrashSuspect = ageMs > 5_000 && confidence === 'alta';
  const logger = realCrashSuspect ? console.warn : console.info;
  const icon = realCrashSuspect ? '⚠️' : 'ℹ️';
  logger(
    `[CrashCapture] ${icon} Silent crash detected — last heartbeat ${ageLabel} ago (${confidence})${memInfo}${cacheInfo}${videoInfo}${trendInfo}`,
  );
}

/**
 * Installs global error handlers. Idempotent — safe to call once at boot.
 */
export function installCrashCapture(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Marca o início desta sessão (boot atual) — base da duração da sessão até um eventual crash.
  sessionStartedAt = Date.now();

  // ANTES DE TUDO: preserva os snapshots crash-time da sessão anterior (contadores/latência/
  // caixa-preta) → chaves `_prev`, ANTES que o tizenAvplay carregue e zere as chaves vivas da
  // nova sessão. Fecha o gap das `_prev` lidas-porém-nunca-escritas (Fase 0).
  preserveCrashTimeSnapshots();

  // Registra ESTE boot no ring de sessões — SEMPRE, com ou sem crash inferido. Tem que vir antes
  // do detectSilentCrash(), que consome/limpa o heartbeat. É o que fecha a subcontagem: saída
  // limpa remove o heartbeat e não gerava registro nenhum (F0-0b).
  recordSessionRestart(getBootId(), getBootSeq());

  // Depois: detecta silent crash do boot anterior antes de qualquer outra
  // coisa rodar (e potencialmente quebrar de novo). Resultado vai pro
  // crashLog onde o overlay pode mostrar.
  detectSilentCrash();

  window.addEventListener('error', (event) => {
    appendCrash({
      timestamp: Date.now(),
      type: 'error',
      message: event.message || 'Unknown error',
      source: event.filename,
      line: event.lineno,
      col: event.colno,
      stack: event.error?.stack,
      // Enriquecimento ADITIVO: device/versão/plataforma/duração/conectividade + breadcrump das
      // últimas linhas de log ("o que estava acontecendo antes"). Best-effort — nunca lança.
      context: buildLiveCrashContext(),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : reason instanceof Error
          ? reason.message
          : 'Unhandled promise rejection';
    appendCrash({
      timestamp: Date.now(),
      type: 'unhandledrejection',
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
      // Mesmo enriquecimento ADITIVO do handler de 'error' acima.
      context: buildLiveCrashContext(),
    });
  });

  // Semeia a 1ª leitura de memória nativa (async) — fica pronta pros próximos ticks.
  refreshNativeMem();
  // Heartbeat tick — escreve a cada HEARTBEAT_INTERVAL_MS. Se app morrer
  // sem chegar no próximo tick, o boot seguinte detecta via timestamp.
  writeHeartbeat(); // primeiro write imediato
  heartbeatTimer = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);

  // Limpa heartbeat em shutdown limpo (visibilitychange hidden + beforeunload)
  // pra evitar false positives quando o usuário fecha o app normalmente.
  // Também limpa o histórico — não é interessante manter heartbeats de
  // sessões antigas misturados com a próxima.
  const markCleanShutdown = (): void => {
    try {
      localStorage.removeItem(HEARTBEAT_KEY);
      localStorage.removeItem(HEARTBEAT_HISTORY_KEY);
    } catch {
      /* ignore */
    }
  };
  window.addEventListener('beforeunload', markCleanShutdown);
  // pagehide não pega em Tizen mas pega em browser/Android — defense in depth
  window.addEventListener('pagehide', markCleanShutdown);

  // Marker that capture is active — appears once per boot.
  console.log(
    `[CrashCapture] ✅ Installed handlers + heartbeat (${HEARTBEAT_INTERVAL_MS}ms)`,
  );
}

/**
 * Para o heartbeat e remove handlers. Usado em testes.
 */
export function uninstallCrashCapture(): void {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  installed = false;
}
